import {
  ACTION,
  ACTION_WITH_PLANT,
  COMMUNICATION,
  plantOutput as PLANT_OUTPUT,
  queueOutput as QUEUE_OUTPUT,
} from '../../data/constants.js';

export const CREATE_INV_MGMT_TRANSACTION = `
    mutation ProductionRequestMutation($input: CreateInvMgmtTransactionInput!) {
        productionRequestMutation {
            createInvMgmtTransaction(input: $input) {
                itemId
                transactionHeaderId
                sourceLineId
                transactionUom
                TransactionQuantity
                transactionInterfaceId
                errorCode
            }
        }
    }
`;
export const CREATE_ACTION_MUTATION = `
    mutation ProductionRequestMutation($input: ActionInput!) {
        productionRequestMutation {
            createAction(input: $input) {
                ${ACTION}
            }
        }
    }
`;

export const UPDATE_ACTION_MUTATION = `
    mutation ProductionRequestMutation($id: String!, $input: ActionUpdateInput!) {
        productionRequestMutation {
            updateActionById(id: $id, input: $input) {
                ${ACTION}
            }
        }
    }
`;

export const GET_ACTION_BY_ID = `
query ProductionRequestQuery($id: String!) {
    productionRequestQuery {
        getActionById(id: $id) {
            ${ACTION}
        }
    }
}
`;
export const GET_OPEN_REQUESTS_BY_ACTION_ID_AND_DYNAMIC_INPUT = `
query ProductionRequestQuery($actionId: String!, $dynamicInput: String!) {
    productionRequestQuery {
        getOpenRequestsByActionIdAndDynamicInput(actionId: $actionId, dynamicInput: $dynamicInput) {
            id
            creationDate
            requestorId
            status
            requestQty
        }
    }
}
`;
export const CREATE_COMMUNICATION_MUTATION = `
    mutation ProductionRequestMutation($requestId: ID!, $communication: String!) {
        productionRequestMutation {
            createCommunication(input: {requestId: $requestId, communication: $communication}) {
                communications {
                    ${COMMUNICATION}
                }
            }
        }
    }
`;
export const MARK_COMMUNICATION_AS_READ_MUTATION = `
    mutation ProductionRequestMutation($requestId: ID!) {
        productionRequestMutation {
            markCommunicationRead(input: {requestId: $requestId}) {
                communications {
                    ${COMMUNICATION}
                }
            }
        }
    }
`;

const DEFAULT_OUTPUT = `
    id
    createdBy
    creationDate
    lastUpdatedBy
    lastUpdatedDate
`;
const LOCATION_OUTPUT = `
    active
    description
    issueToLine
    ${DEFAULT_OUTPUT}
`;
const RESOURCE_OUTPUT = `
    active
    description
    ${DEFAULT_OUTPUT}
`;
const RESOURCE_QUEUE_OUTPUT = `
    queueId
    resourceId
    queueStatus
    active
    createdBy
    creationDate
    lastUpdatedBy
    lastUpdatedDate
`;
const SELECTION_LIST_OUTPUT = `
    description
    ${DEFAULT_OUTPUT}
`;
const DISPOSITION_OUTPUT = `
    dispositionId
    organizationId
    description
    distributionAccount
    segment1
`;
const CARRIER_OUTPUT = `
    carrierId
    scacCode
    freightCode
    carrierName
`;
const INVENTORY_ITEM_OUTPUT = `
    inventoryItemId
    item
    description
`;
const SUBINVENTORY_CODE_OUTPUT = `
    ${INVENTORY_ITEM_OUTPUT}
    subinventoryCode
    qoh
    primaryUomCode
    unitOfIssueCode
    unitOfIssueQoh
    lotNumber
    locatorId
    organizationId
    locator
    inventoryCreation
    fixedlotMultiplier
    transactionUomCode
    unitOfIssueFixedLot
    locatorTypeId
    locatorType
`;
const SUBINVENTORY_INFO_OUTPUT = `
    ${INVENTORY_ITEM_OUTPUT}
    lotNumber
    locatorId
    organizationId
    locator
    subinventoryCode
    qoh
    inventoryCreation
    fixedlotMultiplier
    primaryUomCode
    unitOfIssueCode
    transactionUomCode
    unitOfIssueQoh
    unitOfIssueFixedLot
`;
const LOCATOR_OUTPUT = `
    subinventoryCode
    organizationId
    description
    locatorTypeId
    locatorType
    locator
    locatorId
`;
const RESOURCE_NESTED_OUTPUT = `
    resourceQueues {
        ${RESOURCE_QUEUE_OUTPUT}
        queue {${QUEUE_OUTPUT}}
    }
`;
const QUEUE_NESTED_OUTPUT = `
    resourceQueues {
        ${RESOURCE_QUEUE_OUTPUT}
        resource {${RESOURCE_OUTPUT}}
    }
`;

const query = (name, args, body) => `
    query ProductionRequestQuery${args ? `(${args})` : ''} {
        productionRequestQuery {
            ${name}${body}
        }
    }
`;
const mutation = (name, args, body) => `
    mutation ProductionRequestMutation(${args}) {
        productionRequestMutation {
            ${name}${body}
        }
    }
`;
const selection = (fields) => ` {
                ${fields}
            }`;

export const GET_PLANTS = query('getPlants(input: $input)', '$input: PlantFilterInput', selection(PLANT_OUTPUT));
export const CREATE_PLANT = mutation('createPlant(input: $input)', '$input: PlantInput!', selection(PLANT_OUTPUT));
export const UPDATE_PLANT = mutation(
  'updatePlantById(id: $id, input: $input)',
  '$id: String!, $input: PlantUpdateInput!',
  selection(PLANT_OUTPUT),
);
export const DELETE_PLANT = mutation('deletePlantById(id: $id)', '$id: String!', selection(PLANT_OUTPUT));

export const GET_LOCATIONS_BY_PLANT = query(
  'getLocationsByPlantId(id: $id, input: $input)',
  '$id: String!, $input: LocationFilterInput',
  selection(LOCATION_OUTPUT),
);
export const CREATE_LOCATION = mutation(
  'createLocation(input: $input)',
  '$input: LocationInput!',
  selection(LOCATION_OUTPUT),
);
export const UPDATE_LOCATION = mutation(
  'updateLocationById(id: $id, input: $input)',
  '$id: String!, $input: LocationUpdateInput!',
  selection(LOCATION_OUTPUT),
);
export const DELETE_LOCATION = mutation('deleteLocationById(id: $id)', '$id: String!', selection(LOCATION_OUTPUT));

export const GET_ACTIONS_BY_LOCATION = query(
  'getActionsByLocationId(id: $id, input: $input)',
  '$id: String!, $input: ActionFilterInput',
  selection(ACTION_WITH_PLANT),
);
export const DELETE_ACTION = mutation('deleteActionById(id: $id)', '$id: String!', selection(ACTION));

export const GET_RESOURCE_QUEUES = query(
  'getResourceQueues(input: $input)',
  '$input: ResourceQueueFilterInput',
  selection(RESOURCE_QUEUE_OUTPUT),
);
export const CREATE_RESOURCE_QUEUE = mutation(
  'createResourceQueue(input: $input)',
  '$input: ResourceQueueInput!',
  selection(RESOURCE_QUEUE_OUTPUT),
);
export const UPDATE_RESOURCE_QUEUE = mutation(
  'updateResourceQueueById(queueId: $queueId, resourceId: $resourceId, input: $input)',
  '$queueId: String!, $resourceId: String!, $input: ResourceQueueUpdateInput!',
  selection(RESOURCE_QUEUE_OUTPUT),
);
export const DELETE_RESOURCE_QUEUE = mutation(
  'deleteResourceQueueById(queueId: $queueId, resourceId: $resourceId)',
  '$queueId: String!, $resourceId: String!',
  selection(RESOURCE_QUEUE_OUTPUT),
);

/**
 * Build the resources-by-plant query, optionally selecting the nested resourceQueues.
 * @param {boolean} resourceQueues - include the nested resourceQueues
 * @returns {string}
 */
export const getResourcesByPlantQuery = (resourceQueues) =>
  query(
    'getResourcesByPlantId(id: $id, input: $input)',
    '$id: String!, $input: ResourceFilterInput',
    selection(`${RESOURCE_OUTPUT}${resourceQueues ? RESOURCE_NESTED_OUTPUT : ''}`),
  );
export const CREATE_RESOURCE = mutation(
  'createResource(input: $input)',
  '$input: ResourceInput!',
  selection(RESOURCE_OUTPUT),
);
export const UPDATE_RESOURCE = mutation(
  'updateResourceById(id: $id, input: $input)',
  '$id: String!, $input: ResourceUpdateInput!',
  selection(RESOURCE_OUTPUT),
);
export const DELETE_RESOURCE = mutation('deleteResourceById(id: $id)', '$id: String!', selection(RESOURCE_OUTPUT));

/**
 * Build the queues-by-plant query, optionally selecting the nested resourceQueues.
 * @param {boolean} resourceQueues - include the nested resourceQueues
 * @returns {string}
 */
export const getQueuesByPlantQuery = (resourceQueues) =>
  query(
    'getQueuesByPlantId(id: $id, input: $input)',
    '$id: String!, $input: QueueFilterInput',
    selection(`${QUEUE_OUTPUT}${resourceQueues ? QUEUE_NESTED_OUTPUT : ''}`),
  );
export const CREATE_QUEUE = mutation('createQueue(input: $input)', '$input: QueueInput!', selection(QUEUE_OUTPUT));
export const UPDATE_QUEUE = mutation(
  'updateQueueById(id: $id, input: $input)',
  '$id: String!, $input: QueueUpdateInput!',
  selection(QUEUE_OUTPUT),
);
export const DELETE_QUEUE = mutation('deleteQueueById(id: $id)', '$id: String!', selection(QUEUE_OUTPUT));
export const GET_QUEUES_BY_RESOURCE = query(
  'getQueuesByResourceId(resourceId: $resourceId)',
  '$resourceId: String!',
  '',
);

export const GET_SELECTION_LISTS_BY_PLANT = query(
  'getSelectionListsByPlantId(id: $id)',
  '$id: String!',
  selection(`${SELECTION_LIST_OUTPUT}
                values {
                    ${SELECTION_LIST_OUTPUT}
                }`),
);
export const CREATE_SELECTION_LIST = mutation(
  'createSelectionList(input: $input)',
  '$input: SelectionListInput!',
  selection(SELECTION_LIST_OUTPUT),
);
export const UPDATE_SELECTION_LIST = mutation(
  'updateSelectionListById(id: $id, input: $input)',
  '$id: String!, $input: SelectionListUpdateInput!',
  selection(SELECTION_LIST_OUTPUT),
);
export const DELETE_SELECTION_LIST = mutation(
  'deleteSelectionListById(id: $id)',
  '$id: String!',
  selection(SELECTION_LIST_OUTPUT),
);
export const CREATE_SELECTION_LIST_VALUE = mutation(
  'createSelectionListValue(input: $input)',
  '$input: SelectionListValueInput!',
  selection(SELECTION_LIST_OUTPUT),
);
export const UPDATE_SELECTION_LIST_VALUE = mutation(
  'updateSelectionListValueById(id: $id, input: $input)',
  '$id: String!, $input: SelectionListValueUpdateInput!',
  selection(SELECTION_LIST_OUTPUT),
);
export const DELETE_SELECTION_LIST_VALUE = mutation(
  'deleteSelectionListValueById(id: $id)',
  '$id: String!',
  selection(SELECTION_LIST_OUTPUT),
);

export const GET_REQUESTS_BY_RESOURCE = query(
  'getRequestsByResourceId(resourceId: $resourceId, status: $status)',
  '$resourceId: String!, $status: String',
  '',
);
export const GET_COMPLETED_REQUESTS_BY_RESOURCE = query(
  'getCompletedRequestsByResourceId(resourceId: $resourceId)',
  '$resourceId: String!',
  '',
);
export const GET_REQUESTS_BY_LOCATION = query(
  'getRequestsByLocationIdByStatus(locationId: $locationId)',
  '$locationId: String!',
  '',
);
export const GET_COMPLETED_REQUESTS_BY_LOCATION = query(
  'getCompletedRequestsByLocationIdByStatus(locationId: $locationId)',
  '$locationId: String!',
  '',
);
export const CREATE_REQUEST = mutation(
  'createRequest(input: $input)',
  '$input: RequestInput!',
  selection(`comment
                requestQty
                dynamicInput
                dynamicInputResult
                requestorId
                status
                dropDownListVal1
                dropDownListVal2
                invTransactionId
                ${DEFAULT_OUTPUT}`),
);
export const UPDATE_REQUEST = mutation(
  'updateRequestById(id: $id, input: $input)',
  '$id: String!, $input: RequestUpdateInput!',
  selection('id\n                status'),
);

export const GET_DISPOSITIONS_BY_PLANT = query(
  'getDispositionsByPlantId(id: $id)',
  '$id: String!',
  selection(DISPOSITION_OUTPUT),
);
export const GET_CARRIER_LIST = query('getCarrierList', '', selection(CARRIER_OUTPUT));
export const CREATE_TRAILER_TO_DOCK_TRANSACTION = mutation(
  'createTrailerToDockTransaction(input: $input)',
  '$input: TrailerToDockTransactionInput!',
  '',
);
export const GET_INVENTORY_ITEM_ID_BY_PLANT = query(
  'getInventoryItemIdByPlant(organizationId: $organizationId, item: $item)',
  '$organizationId: Int!, $item: String!',
  selection(INVENTORY_ITEM_OUTPUT),
);
export const GET_SUBINVENTORY_CODES_BY_PLANT = query(
  'getSubinventoryCodesByPlant(organizationId: $organizationId, inventoryItemId: $inventoryItemId, qoh: $qoh)',
  '$organizationId: Int!, $inventoryItemId: Int!, $qoh: quantityOnHandFilter',
  selection(SUBINVENTORY_CODE_OUTPUT),
);
export const GET_SUBINVENTORY_INFO_BY_PLANT = query(
  'getSubinventoryInfoByPlant(organizationId: $organizationId, inventoryItemId: $inventoryItemId, subinventoryCode: $subinventoryCode)',
  '$organizationId: Int!, $inventoryItemId: Int!, $subinventoryCode: String!',
  selection(SUBINVENTORY_INFO_OUTPUT),
);
export const GET_LOCATORS = query(
  'getLocators(organizationId: $organizationId, subinventoryCode: $subinventoryCode, inventoryItemId: $inventoryItemId)',
  '$organizationId: Int!, $subinventoryCode: String!, $inventoryItemId: Int!',
  selection(LOCATOR_OUTPUT),
);
